/**
 * What has changed on the board lately.
 *
 * Ordering only — no React, no fetching. The board is already loaded in full (`useBoard`)
 * and every stamp it ranks on comes back on every ticket, so "what changed lately" is a
 * pass over data we hold rather than a query the server has to grow a route for.
 *
 * It ranks three stamps, and needs all three: `lastActivity` is the newest entry in the
 * ticket's own history and the only one of the three that sees a comment or a link at
 * all, but it is absent on the migrated tickets, whose history stayed in Notion — so
 * `createdAt`/`updatedAt` remain the floor. The server computes `lastActivity`
 * (`src/repo.ts`), so what lives here is the COMBINATION and nothing else.
 */

import type { Ticket } from './api';

/** Twenty rows is a screenful and the answer to "what did I miss?" — not an archive. */
export const RECENT_LIMIT = 20;

export interface Touched {
  ticket: Ticket;
  /** Epoch ms of the change that ranked it, or null if no stamp on it is a date. */
  at: number | null;
  /**
   * What that change was, in the activity log's own wording where there is one —
   * `commented`, `linked blocks DWLF-12` — and `created` or `edited` where the ranking
   * stamp was one of the ticket's own.
   */
  kind: string;

  /**
   * Who did it, or null for a ticket with no history — the migrated ones, ranked on
   * `createdAt`/`updatedAt`, which date a change without recording who made it.
   */
  actor: string | null;
}

export interface RecentTickets {
  rows: Touched[];
  /**
   * How many tickets were eligible before the limit — so the view can say it is showing
   * twenty OF something. A truncated list that looks complete is the failure this board
   * has a house rule about (HR-2).
   */
  total: number;
}

/**
 * Parsed rather than string-compared: every timestamp the API writes is
 * `new Date().toISOString()`, but `createdAt` on the migrated tickets came from Notion,
 * and lexical order is only chronological while every value shares one format and zone.
 *
 * Null means the stamp is not a date, and callers render it as unknown rather than as a
 * time. It sorts oldest.
 */
function epochMs(iso: string | undefined): number | null {
  const ms = Date.parse(iso ?? '');
  return Number.isNaN(ms) ? null : ms;
}

/**
 * An actor as a row can afford to print it: the mailbox, keeping any `via <token>` that
 * says an agent rather than a person did it. The full address is the cell's `title`.
 */
export function actorLabel(actor: string): string {
  return actor.replace(/@[^\s]+/, '');
}

/**
 * The activity log's word for a change, in the column's vocabulary.
 *
 * Only `updated` is translated. The log says `updated` where a ticket with no history to
 * rank on — a migrated one, read off `updatedAt` — says `edited`, and one column reading
 * two words for one thing invites the question of what the difference is. Everything
 * else (`commented`, `linked blocks DWLF-12`) is the log's own wording, which is more
 * specific than anything this could map it to.
 */
export function actionLabel(action: string | undefined): string {
  if (action === undefined) return 'changed';
  return action === 'updated' ? 'edited' : action;
}

/**
 * When a ticket last changed, and what that change was.
 *
 * Ties go to the activity row, which is what makes a newly-filed ticket read `created`
 * rather than `edited`: creation writes both stamps and an activity row in one
 * transaction, so all three carry the same instant and only one of them says which.
 *
 * `updatedAt` normally beats `createdAt` on its own, since it is stamped at creation and
 * only moves forward; taking the later of the two keeps the ordering honest if a record
 * ever carries a `createdAt` from elsewhere that sits ahead of it — which is exactly what
 * migration did, in the other direction.
 */
function touched(ticket: Ticket): Touched {
  const created = epochMs(ticket.createdAt);
  const updated = epochMs(ticket.updatedAt);
  const activity = epochMs(ticket.lastActivity?.at);

  const fields: Touched =
    updated !== null && (created === null || updated > created)
      ? { ticket, at: updated, kind: 'edited', actor: null }
      : { ticket, at: created, kind: 'created', actor: null };

  if (activity === null || (fields.at !== null && activity < fields.at)) return fields;
  return {
    ticket,
    at: activity,
    kind: actionLabel(ticket.lastActivity?.action),
    actor: ticket.lastActivity?.actor ?? null,
  };
}

/**
 * The most recently changed tickets first, newest to oldest.
 *
 * Archived tickets are left out rather than dimmed as the Table does: soft-archiving
 * STAMPS `updatedAt` (`src/softArchive.ts`), so an archived ticket sorts straight to the
 * top at the exact moment it stopped being work — spending one of twenty rows to say so.
 *
 * Ties break on ref, descending. The 89 migrated tickets share one `updatedAt` to the
 * millisecond, so without a second key their order would be whatever the sort happened to
 * preserve — stable today, and quietly not on the day the input order changes.
 */
export function recentlyTouched(tickets: Ticket[], limit = RECENT_LIMIT): RecentTickets {
  const eligible = tickets.filter((ticket) => !ticket.archived).map(touched);
  const rows = eligible
    .sort((a, b) => (b.at ?? 0) - (a.at ?? 0) || b.ticket.ref - a.ticket.ref)
    .slice(0, limit);
  return { rows, total: eligible.length };
}
